import { civiliteObject } from '../models/model/concretes/shared/civility.model';

export const FRANCE_COUNTRY_NAME = 'France';

/**
 * Données souscripteur issues du contexte (API /contexts).
 * Seuls les champs utiles au contrôle de complétude sont typés ici.
 */
export interface SubscriberContextData {
  civilite?: number | string | null;
  nom?: string | null;
  prenom?: string | null;
  dateNaissance?: string | null;
  paysNaissance?: string | null;
  villeNaissance?: string | null;
  codePostalNaissance?: string | null;
  nationalite?: string | null;
}

/**
 * Correspondance entre les contrôles du formulaire souscripteur et les champs du contexte.
 */
const CONTEXT_FIELD_BY_FORM_CONTROL: Record<string, keyof SubscriberContextData> = {
  civility: 'civilite',
  lastname: 'nom',
  firstname: 'prenom',
  birth_date: 'dateNaissance',
  birth_country: 'paysNaissance',
  birth_city: 'villeNaissance',
  birth_postal_code: 'codePostalNaissance',
  nationality: 'nationalite',
};

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === '';
}

function isContextValuePresent(field: keyof SubscriberContextData, value: unknown): boolean {
  if (field === 'civilite') {
    return civiliteObject.some((civility) => String(civility.id) === String(value));
  }
  return !isEmptyValue(value);
}

/**
 * Indique si la naissance est en France (code postal de naissance attendu).
 */
export function isFrenchBirthCountry(paysNaissance: string | null | undefined): boolean {
  return paysNaissance === FRANCE_COUNTRY_NAME;
}

/**
 * Retourne la liste des champs obligatoires absents du contexte.
 * Le code postal de naissance n'est obligatoire qu'en cas de naissance en France.
 */
export function getMissingSubscriberFields(
  souscripteur: SubscriberContextData | null | undefined,
): string[] {
  const data = souscripteur ?? {};
  const requiredFields: [keyof SubscriberContextData, unknown][] = [
    ['civilite', data.civilite],
    ['nom', data.nom],
    ['prenom', data.prenom],
    ['dateNaissance', data.dateNaissance],
    ['paysNaissance', data.paysNaissance],
    ['villeNaissance', data.villeNaissance],
    ['nationalite', data.nationalite],
  ];

  if (isFrenchBirthCountry(data.paysNaissance)) {
    requiredFields.push(['codePostalNaissance', data.codePostalNaissance]);
  }

  return requiredFields
    .filter(([field, value]) => !isContextValuePresent(field, value))
    .map(([field]) => field);
}

/**
 * Vrai lorsque toutes les données personnelles obligatoires sont présentes dans le contexte.
 */
export function isSubscriberContextComplete(
  souscripteur: SubscriberContextData | null | undefined,
): boolean {
  return getMissingSubscriberFields(souscripteur).length === 0;
}

/**
 * Indique si le contrôle du formulaire souscripteur est fourni par le contexte.
 */
export function isProvidedByContext(
  souscripteur: SubscriberContextData | null | undefined,
  controlName: string,
): boolean {
  const field = CONTEXT_FIELD_BY_FORM_CONTROL[controlName];
  if (!field) return false;

  return isContextValuePresent(field, souscripteur?.[field]);
}
